/**
 * Sales endpoints, kept apart from api.ts so the sales dashboard's types do
 * not swell a file every other tab already shares. Same `request`, same
 * error shape.
 */

import { request } from './api';

export interface SalesReviewSettings {
  toleranceMinor: number;
  tolerancePct: number;
  partialPaymentGraceDays: number;
  depositGraceDays: number;
  longPaymentDays: number;
  duplicateWindowMinutes: number;
}

export interface SalesSettings {
  historyYears: number;
  pruneOutsideHistory: boolean;
  staleAfterMinutes: number;
  openOrderStaleDays: number;
  depositChannelIds: string[];
  intradayBackfillMinutes: number;
  nightlyBackfillMinutes: number;
  deadStockDays: number;
  review: SalesReviewSettings;
  insights: SalesInsightSettings;
}

/** When an insight is worth showing (lib/sales/settings.ts on the API). */
export interface SalesInsightSettings {
  surgeMultiple: number;
  surgeMinUnits: number;
  trendChangePct: number;
  trendMinOrders: number;
  mixShiftPts: number;
  refundMultiple: number;
  refundMinMinor: number;
  feeJumpPts: number;
  openOrderAgingDays: number;
  reviewBacklogPct: number;
  forecastMissPct: number;
  topSellers: number;
}

export type SalesSettingsPatch = Partial<Omit<SalesSettings, 'review' | 'insights'>> & {
  review?: Partial<SalesReviewSettings>;
  insights?: Partial<SalesInsightSettings>;
};

export interface SalesSyncRunSummary {
  id: string;
  mode: string;
  startedAt: string;
  finishedAt: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  seen: number;
  created: number;
  updated: number;
  ok: boolean;
  error: string | null;
}

export interface SalesChannelStatus {
  channelId: string;
  name: string;
  type: string;
  enabled: boolean;
  capabilities: {
    orders: boolean;
    fees: 'with_order' | 'ledger' | 'none';
    recognitionBasis: 'payments' | 'order_total';
    unsupportedReason: string | null;
  };
  depositsExpected: boolean;
  state: {
    supported: boolean;
    unsupportedReason: string | null;
    backfillStatus: 'pending' | 'running' | 'complete' | 'failed' | 'blocked';
    backfillTargetFrom: string | null;
    incrementalStartedAt: string | null;
    watermark: string | null;
    lastRunAt: string | null;
    lastSuccessAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    consecutiveFailures: number;
  } | null;
  coverage: {
    earliest: string | null;
    latest: string | null;
    intervals: number;
    backfillPct: number | null;
  };
  orders: { total: number; countable: number; earliest: string | null; latest: string | null };
  openIssues: { conflict: number; warning: number; info: number };
  recentRuns: SalesSyncRunSummary[];
}

export interface SalesSyncResult {
  channelId: string;
  channelName: string;
  ok: boolean;
  skipped?: string;
  error?: string;
  evaluated: number;
  durationMs: number;
}

export type SalesPaymentState = 'none' | 'partial' | 'full' | 'over';
export type RuleTreatment = 'count_payments' | 'count_order_total' | 'exclude';

export interface ProfileIssue {
  ruleId: string;
  open: number;
  openAmountAtRiskMinor: number;
  ruleResolved: number;
}

export interface ProfileRow {
  year: number;
  channelStatus: string;
  paymentState: SalesPaymentState;
  orders: number;
  countable: number;
  withRefunds: number;
  totalMinor: number;
  paidMinor: number;
  refundedMinor: number;
  recognizedMinor: number;
  issues: ProfileIssue[];
}

export interface ReviewRuleInfo {
  id: string;
  label: string;
  severity: 'conflict' | 'warning' | 'info';
  description: string;
}

export interface MonthTotals {
  orders: number;
  orderTotalMinor: number;
  receivedMinor: number;
  refundedMinor: number;
}

export interface ReconciliationRow {
  month: string;
  site: MonthTotals;
  imported: MonthTotals;
  coverage: number;
  matches: boolean;
}

export interface ResolutionMatch {
  ruleIds: string[];
  channelIds?: string[];
  placedFrom?: string;
  placedTo?: string;
  channelStatuses?: string[];
  paymentStates?: SalesPaymentState[];
}

export interface ResolutionRule {
  id: string;
  name: string;
  match: ResolutionMatch;
  treatment: RuleTreatment;
  note: string;
  enabled: boolean;
  createdById: string | null;
  createdAt: string;
  applied: number;
}

export interface RulePreview {
  issues: number;
  orders: number;
  amountAtRiskMinor: number;
  decidedByPerson: number;
  decidedByOtherRule: number;
}

export const salesApi = {
  status: () => request<{ settings: SalesSettings; channels: SalesChannelStatus[] }>('/api/admin/sales/status'),

  saveSettings: (patch: SalesSettingsPatch) =>
    request<{ settings: SalesSettings }>('/api/admin/sales/settings', { method: 'PUT', body: JSON.stringify(patch) }),

  syncNow: (channelIds?: string[]) =>
    request<{ results: SalesSyncResult[] }>('/api/admin/sales/sync', {
      method: 'POST',
      body: JSON.stringify({ channelIds }),
    }),

  restartBackfill: (channelId: string) =>
    request<{ ok: true }>(`/api/admin/sales/channels/${encodeURIComponent(channelId)}/restart-backfill`, {
      method: 'POST',
    }),

  recognitionProfile: (channelId: string) =>
    request<{ channelId: string; rows: ProfileRow[]; rules: ReviewRuleInfo[] }>(
      `/api/admin/sales/recognition-profile?channelId=${encodeURIComponent(channelId)}`,
    ),

  reconcile: (channelId: string) =>
    request<{ months: ReconciliationRow[] }>(`/api/admin/sales/channels/${encodeURIComponent(channelId)}/reconcile`),

  resolutionRules: () => request<{ rules: ResolutionRule[] }>('/api/admin/sales/resolution-rules'),

  previewRule: (match: ResolutionMatch) =>
    request<RulePreview>('/api/admin/sales/resolution-rules/preview', { method: 'POST', body: JSON.stringify({ match }) }),

  createRule: (rule: { name: string; match: ResolutionMatch; treatment: RuleTreatment; note: string }) =>
    request<{ applied: number }>('/api/admin/sales/resolution-rules', { method: 'POST', body: JSON.stringify(rule) }),

  revokeRule: (id: string) =>
    request<{ reopened: number }>(`/api/admin/sales/resolution-rules/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),

  reevaluate: () =>
    request<{ evaluated: number; failed: number; firstError: string | null }>('/api/admin/sales/reevaluate', {
      method: 'POST',
    }),
};

// ---- Google Analytics (Admin → Sales data) ----------------------------------------

export interface AnalyticsSite {
  siteChannelId: string;
  propertyId: string;
  enabled: boolean;
}

export interface AnalyticsSettings {
  sites: AnalyticsSite[];
  useSheetServiceAccount: boolean;
  serviceAccountEmail: string;
  hasServiceAccountKey: boolean;
  sheetServiceAccountEmail: string | null;
  itemIdMatches: 'sku' | 'external_id';
}

export interface AnalyticsWebsite {
  channelId: string;
  name: string;
  site: 'mlf' | 'mff' | null;
  configured: boolean;
  historyFrom: string | null;
  dataThrough: string | null;
  gaps: number;
  backfillStatus: string | null;
  propertyCreated: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
}

export interface AnalyticsTestResult {
  siteChannelId: string;
  propertyId: string;
  ok: boolean;
  error?: string;
  displayName?: string;
  timeZone?: string | null;
  currencyCode?: string | null;
  createdOn?: string | null;
  last30?: { sessions: number; addToCarts: number; purchases: number; revenueMinor: number };
  ecommerce?: boolean;
  warnings?: string[];
}

export const analyticsApi = {
  get: () => request<{ settings: AnalyticsSettings; websites: AnalyticsWebsite[] }>('/api/admin/analytics'),
  save: (patch: {
    sites?: AnalyticsSite[];
    useSheetServiceAccount?: boolean;
    serviceAccountJson?: string;
    itemIdMatches?: 'sku' | 'external_id';
  }) => request<{ settings: AnalyticsSettings }>('/api/admin/analytics', { method: 'PUT', body: JSON.stringify(patch) }),
  test: () => request<{ serviceAccount: string | null; results: AnalyticsTestResult[] }>('/api/admin/analytics/test', { method: 'POST', body: '{}' }),
  sync: (channelIds?: string[]) =>
    request<{ results: Array<{ channelId: string; channelName: string; error?: string; skipped?: string; rows: number }> }>('/api/admin/analytics/sync', {
      method: 'POST',
      body: JSON.stringify({ channelIds }),
    }),
  restart: (channelId: string) => request<{ ok: true }>(`/api/admin/analytics/backfill/${encodeURIComponent(channelId)}`, { method: 'POST', body: '{}' }),
};
